import { saveAs } from 'file-saver';

import { environment } from '../../environment';
import { logger } from '../../features/logger';
import { autoSaveError, generalErrorDialog } from '../../shared/dialogConfigs';
import { apiEventReset, isError, isSaving, isSubmitted, isSubmitting, uploadIsLoading } from '../../store/apiEvent';
import { clearStagingAttachments, getAttachments } from '../../store/attachments';
import { updatePolicyStatus } from '../../store/common';
import {
  checkLineofCredit,
  getCustomer,
  submitCustomer,
  updateCustomer,
  updateCustomerFinancialCheck,
  validateCustomer
} from '../../store/customer';
import { closeDialog, openDialog } from '../../store/dialog';
import { saveCountryList, saveStateList } from '../../store/odata';
import { timeoutPromise } from '../../utils/hooksLib';
import { updateSessionStorage } from '../../utils/storage';
import { getBrowserName } from '../../utils/tools';
import APIConfigService from '../APIConfigService';

// const environment = require('../../environment');
const { hostedKey } = environment().smartyStreet;

class APIService {
  /**
   * Create an APIService instance
   * @param {APIClient} apiClient - Instance with access token to make requests to our backend
   */
  constructor(apiClient) {
    this.apiClient = apiClient;
  }

  /**
   * @param {function} dispatch - function to trigger and update reducers
   * @returns {Promise} - With this promise we can add on success functionality with .then and on fail with .catch
   */
  async getCustomerDetails(dispatch) {
    const hcId = updateSessionStorage.getItem('hcId');
    const customerUrl = `${APIConfigService.getCustomer().url}/${hcId}`;
    const attachmentsUrl = `${APIConfigService.getAttachments().url}/${hcId}`;
    const odataUrl = APIConfigService.getOdataCountry().url;
    const policyUrl = APIConfigService.privacyPolicy().url;

    try {
      // Request customer details, attachments, and signatureStatus
      const [customer, attachments, odataCountryList, policyResponse] = await Promise.all([
        this.apiClient.get(customerUrl),
        this.apiClient.get(attachmentsUrl),
        this.apiClient.get(odataUrl),
        this.apiClient.get(policyUrl)
      ]);
      dispatch(getCustomer(customer));
      dispatch(updateCustomerFinancialCheck(customer.customerOptoutAttachments));
      dispatch(checkLineofCredit(customer.lineOfCredit));
      // Update attachments reducer with response data
      dispatch(getAttachments(attachments));
      dispatch(saveCountryList({ dataList: odataCountryList.data }));

      // Request policy response
      const { response: policyStatus } = policyResponse;
      // Returns promise to add optional success callback
      dispatch(updatePolicyStatus(policyStatus));

      // Returns promise to add optional success callback
      return new Promise(res => res());
    } catch (error) {
      logger.error(`Red-Carpet-FE-API getCustomerDetails - FAILED`, { error });
      dispatch(isError());
      // Returns promise to add optional fail callback
      return new Promise((_, rej) => rej(error));
    }
  }

  /**
   * @param {function} dispatch - function to trigger and update reducers
   * @returns {Promise} - With this promise we can add on success functionality with .then and on fail with .catch
   */
  async getCustomer(dispatch) {
    const hcId = updateSessionStorage.getItem('hcId');
    const customerUrl = `${APIConfigService.getCustomer().url}/${hcId}`;

    try {
      // Request customer
      const customer = await this.apiClient.get(customerUrl);
      //update bank Reference with clabe field
      const { bankReference: { bankKey = '', accountNumber = '' } = {} } = customer;
      customer.bankReference = {
        ...customer.bankReference,
        clabe: `${bankKey || ''}${accountNumber || ''}`
      };
      dispatch(getCustomer(customer));

      // Returns promise to add optional success callback
      return new Promise(res => res());
    } catch (error) {
      logger.error(`Red-Carpet-FE-API getCustomer - FAILED`, { error });
      dispatch(isError());
      // Returns promise to add optional fail callback
      return new Promise((_, rej) => rej(error));
    }
  }

  /**
   * @param {function} dispatch - function to trigger and update reducers
   * @returns {Promise} - With this promise we can add on success functionality with .then and on fail with .catch
   */
  async getActiveRequests(dispatch) {
    const activeRequestUrl = APIConfigService.getActiveRequests().url;

    try {
      // Request customer
      const requests = await this.apiClient.get(activeRequestUrl);
      // Returns promise to add optional success callback
      return new Promise(res => res(requests));
    } catch (error) {
      logger.error(`Red-Carpet-FE-API getActiveRequests - FAILED`, { error });
      dispatch(isError());
      // Returns promise to add optional fail callback
      return new Promise((_, rej) => rej(error));
    }
  }

  /**
   * @param {Object} values - The customer values you want to update in the backend
   * @param {function} dispatch - function to trigger and update reducers
   * @returns {Promise} - With this promise we can add on success functionality with .then and on fail with .catch
   */
  async updateCustomer(values, dispatch) {
    if (values.countryCode === 'MX' && values.bankReference && 'clabe' in values.bankReference) {
      const { clabe = '', accountNumber } = values.bankReference;

      // Update api payload
      const key = clabe.substr(0, 6);
      // const accNumber = clabe.substr(6);
      values.bankReference.bankKey = key;
      values.bankReference.accountNumber = accountNumber;
    }

    const { url, timeout } = APIConfigService.updateCustomer();
    dispatch(isSaving());
    try {
      const customer = await timeoutPromise(timeout, this.apiClient.put(url, values));
      dispatch(updateCustomer(customer));

      // Reset save event properties after 3 seconds (use for toggling UI)
      setTimeout(() => {
        dispatch(apiEventReset());
      }, 1000);

      // Returns promise to add optional success callback
      return new Promise(res => res());
    } catch (error) {
      logger.error(`Red-Carpet-FE-API updateCustomer - FAILED`, { error });
      dispatch(isError());
      dispatch(openDialog(autoSaveError(this)));
      // Returns promise to add optional fail callback
      return new Promise((_, rej) => rej());
    }
  }

  /**
   * @param {Object} values - The customer values you to submit to the backend
   * @param {function} dispatch - function to trigger and update reducers
   * @returns {Promise} - With this promise we can add on success functionality with .then and on fail with .catch
   */
  async submitCustomer(values, dispatch) {
    if (values.bankReference && 'clabe' in values.bankReference) {
      delete values.bankReference.clabe;
    }
    const url = APIConfigService.submitCustomer().url;
    dispatch(isSubmitting());
    try {
      const customer = await this.apiClient.post(url, values);
      dispatch(submitCustomer(customer));
      dispatch(isSubmitted());

      // Reset save event properties after 3 seconds (use for toggling UI)
      setTimeout(() => {
        dispatch(apiEventReset());
      }, 3000);

      // Returns promise to add optional success callback
      return new Promise(res => res());
    } catch (error) {
      logger.error(`Red-Carpet-FE-API submitCustomer - FAILED`, { error });
      dispatch(isError());
      // Returns promise to add optional fail callback
      return new Promise((_, rej) => rej(error));
    }
  }

  /**
   * @param {Object} values - The customer values you to submit to the backend
   * @param {function} dispatch - function to trigger and update reducers
   * @returns {Promise} - With this promise we can add on success functionality with .then and on fail with .catch
   */
  async validateCustomer(values, dispatch) {
    if (values.bankReference && 'clabe' in values.bankReference) {
      delete values.bankReference.clabe;
    }
    const url = APIConfigService.validateCustomer().url;

    try {
      const customer = await this.apiClient.post(url, values);
      dispatch(validateCustomer(customer));

      // Reset save event properties after 3 seconds (use for toggling UI)
      setTimeout(() => {
        dispatch(apiEventReset());
      }, 3000);

      // Returns promise to add optional success callback
      return new Promise(res => res());
    } catch (error) {
      logger.error(`Red-Carpet-FE-API validateCustomer - FAILED`, { error });
      dispatch(isError());
      // Returns promise to add optional fail callback
      return new Promise((_, rej) => rej(error));
    }
  }

  /**
   * @param {function} dispatch - function to trigger and update reducers
   * @returns {Promise} - With this promise we can add on success functionality with .then and on fail with .catch
   */
  async getExistingAttachments(dispatch) {
    const hcId = updateSessionStorage.getItem('hcId');
    const url = `${APIConfigService.getAttachments().url}/${hcId}`;
    try {
      const attachments = await this.apiClient.get(url);
      dispatch(getAttachments(attachments || []));

      // Returns promise to add optional success callback
      return new Promise(res => res());
    } catch (error) {
      logger.error(`Red-Carpet-FE-API getExistingAttachments - FAILED`, {
        error
      });
      dispatch(isError());
      // Returns promise to add optional fail callback
      return new Promise((_, rej) => rej());
    }
  }

  /**
   * @param {function} dispatch - function to trigger and update reducers
   * @returns {Promise} - With this promise we can add on success functionality with .then and on fail with .catch
   */
  async getOdataList(dispatch, cardCategory, index, countryCode = '') {
    const { getOdataCountry, getOdataState } = APIConfigService;
    const getApiUrlMethod = countryCode ? getOdataState : getOdataCountry;
    const url = getApiUrlMethod(countryCode).url;
    const updateStore = countryCode ? saveStateList : saveCountryList;
    try {
      const { data } = await this.apiClient.get(url);
      dispatch(updateStore({ dataList: data, cardCategory, index }));
      // Returns promise to add optional success callback
      return new Promise(res => res());
    } catch (error) {
      logger.error(`Red-Carpet-FE-API getOdataList - FAILED`, { error });
      dispatch(isError());
      // Returns promise to add optional fail callback
      return new Promise((_, rej) => rej());
    }
  }

  /**
   * @param {FormData} formData - FormData with attachment files
   * @param {function} dispatch - function to trigger and update reducers
   * @returns {Promise} - With this promise we can add on success functionality with .then and on fail with .catch
   */
  async uploadAttachments(formData, dispatch) {
    dispatch(uploadIsLoading());

    const url = APIConfigService.uploadAttachments().url;
    try {
      await this.apiClient.upload(url, formData);
      // Clear loading event
      dispatch(apiEventReset());

      // Clear staging files in store
      dispatch(clearStagingAttachments());
      dispatch(closeDialog());

      // Get all attachments to refresh ui since the post request returns only success status
      this.getExistingAttachments(dispatch);
      // Returns promise to add optional success callback
      return new Promise(res => res());
    } catch (error) {
      logger.error(`Red-Carpet-FE-API uploadAttachments - FAILED`, { error });
      dispatch(isError());
      // Returns promise to add optional fail callback
      return new Promise((_, rej) => rej());
    }
  }

  /**
   * @param {Object} values - The help request values you to submit to the backend
   * @param {function} dispatch - function to trigger and update reducers
   * @returns {Promise} - With this promise we can add on success functionality with .then and on fail with .catch
   */
  async submitHelpRequest(values, dispatch) {
    if (values.bankReference && 'clabe' in values.bankReference) {
      delete values.bankReference.clabe;
    }
    const url = APIConfigService.submitHelpRequest().url;

    dispatch(isSubmitting());

    try {
      await Promise.resolve(this.apiClient.post(url, values));

      // Reset save event properties after 3 seconds (use for toggling UI)
      setTimeout(() => {
        dispatch(apiEventReset());
      }, 3000);

      return new Promise(res => res());
    } catch (error) {
      logger.error(`Red-Carpet-FE-API submitHelpRequest - FAILED`, { error });
      dispatch(apiEventReset());
      dispatch(isError());
      // Returns promise to add optional fail callback
      return new Promise((_, rej) => rej());
    }
  }

  /**
   * @returns {Promise} - With this promise we can add on success functionality with .then and on fail with .catch
   */
  async revokeToken() {
    const url = APIConfigService.revokeToken().url;

    try {
      await this.apiClient.delete(url);
      // Returns promise to add optional success callback
      return new Promise(res => res());
    } catch (error) {
      logger.error(`Red-Carpet-FE-API revokeToken - FAILED`, { error });
      // Returns promise to add optional fail callbac
      return new Promise((_, rej) => rej());
    }
  }

  /**
   * @param {String} fileName - Name of the file to remove
   * @param {function} dispatch - function to trigger and update reducers
   * @returns {Promise} - With this promise we can add on success functionality with .then and on fail with .catch
   */
  async removeAttachments(fileName, dispatch) {
    const hcId = updateSessionStorage.getItem('hcId');
    const url = APIConfigService.removeAttachments(fileName, hcId).url;
    try {
      await this.apiClient.delete(url);

      // Get all attachments to refresh ui
      this.getExistingAttachments(dispatch);
      // Returns promise to add optional success callback
      return new Promise(res => res());
    } catch (error) {
      logger.error(`Red-Carpet-FE-API removeAttachments - FAILED`, { error });
      dispatch(isError());
      // Returns promise to add optional fail callback
      return new Promise((_, rej) => rej());
    }
  }

  /**
   * * @param {String} hcId - Honey comb id
   * @param {String} fileName - Name of the file to remove
   * @param {function} dispatch - function to trigger and update reducers
   * @returns {Promise} - With this promise we can add on success functionality with .then and on fail with .catch
   */
  async downloadFile(hcId, fileName, dispatch) {
    try {
      // Request file blob
      const { timeout, url } = APIConfigService.downloadAttachments(fileName, hcId);
      const response = await timeoutPromise(timeout, this.apiClient.get(url, true));
      //converting to blob
      const file = await response.blob();
      saveAs(file, fileName);
      // Returns promise to add optional success callback
      return new Promise(res => res());
    } catch (error) {
      logger.error(`Red-Carpet-FE-API downloadFile - FAILED`, { error });
      dispatch(isError());
      dispatch(openDialog(generalErrorDialog(error)));
      // Returns promise to add optional fail callback
      // return new Promise((_, rej) => rej(error));
    }
  }

  /**
   *
   * @param {String} address - the address to send to smarty streets
   * @returns {Promise} - Return promise that when resolved will hold address suggestions
   */
  async getSmartyStreets(address) {
    const url = `https://us-autocomplete-pro.api.smartystreets.com/lookup?key=${
      import.meta.env.VITE_SS_KEY || hostedKey
    }&search=${address.split(' ').join('+')}&prefer_geolocation=city`;

    return this.apiClient.getSmartyAPI(url);
  }

  /**
   * @param {function} dispatch - function to trigger and update reducers
   * @returns {Promise} - With this promise we can add on success functionality with .then and on fail with .catch
   */
  async savePolicyResponse(requestId, dispatch) {
    const policyUrl = APIConfigService.privacyPolicy().url;

    try {
      const requestData = {
        timeStamp: new Date().getTime(),
        requestId,
        device: getBrowserName()
      };
      // Request policy response
      const policyResponse = await this.apiClient.put(policyUrl, requestData);
      const { response: policyStatus } = policyResponse;
      // Returns promise to add optional success callback
      dispatch(updatePolicyStatus(policyStatus));
      // Returns promise to add optional success callback
      return new Promise(res => res());
    } catch (error) {
      logger.error(`Red-Carpet-FE-API savePolicyResponse - FAILED`, { error });
      dispatch(isError());
      // Returns promise to add optional fail callback
      return new Promise((_, rej) => rej(error));
    }
  }

  // /**
  //  * @param {function} dispatch - function to trigger and update reducers
  //  * @returns {Promise} - With this promise we can add on success functionality with .then and on fail with .catch
  //  */
  // async getPolicyResponse(dispatch) {
  //   const policyUrl = APIConfigService.privacyPolicy().url;

  //   try {
  //     // Request policy response
  //     const policyResponse = await this.apiClient.get(policyUrl);
  //     const { response: policyStatus } = policyResponse;
  //     // Returns promise to add optional success callback
  //     dispatch(updatePolicyStatus(policyStatus));
  //     return new Promise((res) => res());
  //   } catch (error) {
  //     dispatch(isError());
  //     // Returns promise to add optional fail callback
  //     return new Promise((_, rej) => rej(error));
  //   }
  // }
}

export default APIService;
